// Compact card for the generic Board/Calendar views. Source-agnostic: resolves
// names/projects via ResolverContext, edits via ctx.patch. Mirrors RecordRow's
// data but in a card layout.

import { Bot, Check, Circle, Diamond, Flag } from "lucide-preact";
import { TASK_PRIORITY_LABEL, TASK_PRIORITY_VAR, formatDateShort, initials, localTodayIso, softPillStyle } from "../lib/format";
import type { ResolverContext, TaskRecord } from "../lib/taskRecord";
import { isBotUser, userTitle } from "./BotBadge";

interface Props {
  record: TaskRecord;
  ctx: ResolverContext;
  readOnly?: boolean;
  showProject?: boolean;
  showStatus?: boolean;
  onOpen?: (record: TaskRecord) => void;
  /** dnd props — spread on the outer card (touch-capable board drag). */
  draggableProps?: Record<string, unknown>;
  dragHandleProps?: Record<string, unknown>;
  innerRef?: (el: HTMLElement | null) => void;
  isDragging?: boolean;
  style?: preact.JSX.CSSProperties;
}

export function RecordCard(props: Props): preact.JSX.Element {
  const { record, ctx, readOnly = false, showProject = false, showStatus = false } = props;
  const { draggableProps, dragHandleProps, innerRef, isDragging, style } = props;
  const project = record.projectId !== null ? ctx.resolveProject(record.projectId) : undefined;
  const overdue = !record.done && record.dueDate && record.dueDate < localTodayIso();

  return (
    <div
      ref={(el) => innerRef?.(el as HTMLElement | null)}
      {...(draggableProps ?? {})}
      {...(dragHandleProps ?? {})}
      style={style}
      class={`rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-2.5 hover:shadow-level-1 cursor-pointer flex flex-col gap-1.5 ${
        isDragging ? "shadow-level-3 rotate-[0.5deg]" : ""
      }`}
      onClick={() => props.onOpen?.(record)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          props.onOpen?.(record);
        }
      }}
    >
      <div class="flex items-start gap-1.5">
        {record.isMilestone ? (
          <Diamond size={14} class="mt-0.5 shrink-0 text-[color:var(--color-prio-urgent)]" fill="currentColor" aria-hidden="true" />
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!readOnly) void ctx.patch(record, { done: !record.done });
            }}
            disabled={readOnly}
            class="mt-0.5 shrink-0 inline-flex items-center justify-center size-5 rounded-full text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-status-done)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:cursor-default"
            aria-label={record.done ? "Als offen markieren" : "Als erledigt markieren"}
            aria-pressed={record.done}
          >
            {record.done ? <Check size={14} class="text-[color:var(--color-status-done)]" /> : <Circle size={14} />}
          </button>
        )}
        <span class={`text-xs leading-snug ${record.done ? "line-through opacity-60" : "text-[color:var(--color-on-surface)]"}`}>
          {record.title}
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-1">
        {showStatus && (
          <span class="inline-flex items-center rounded px-1.5 py-0.5 text-label font-semibold" style={softPillStyle(record.status.color)}>
            {record.status.label}
          </span>
        )}
        {record.priority && (
          <span class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-label font-semibold" style={softPillStyle(TASK_PRIORITY_VAR[record.priority])}>
            <Flag size={14} class="shrink-0" fill="currentColor" aria-hidden="true" />
            {TASK_PRIORITY_LABEL[record.priority]}
          </span>
        )}
        {showProject && project && (
          <span class="inline-flex items-center rounded px-1.5 py-0.5 text-label font-medium max-w-[110px] truncate" style={softPillStyle(project.color ?? "var(--color-primary)")} title={project.name}>
            {project.name}
          </span>
        )}
        {record.dueDate && (
          <span
            class={`text-label tabular-nums ${overdue ? "text-[color:var(--color-prio-urgent)] font-semibold" : "text-[color:var(--color-on-surface-variant)]"}`}
            title={overdue ? "Überfällig" : "Fälligkeit"}
          >
            {formatDateShort(record.dueDate)}
          </span>
        )}
        {record.assigneeIds.length > 0 && (
          // Bot-Zuständige: Glyph + tertiary statt Initialen (siehe BotBadge).
          <span
            class={`ml-auto inline-flex items-center justify-center size-5 rounded-full text-label font-semibold ${
              isBotUser(record.assigneeIds[0])
                ? "bg-[color:var(--color-tertiary-container)] text-[color:var(--color-on-tertiary-container)]"
                : "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)]"
            }`}
            title={userTitle(
              record.assigneeIds[0],
              ctx.resolveUserName(record.assigneeIds[0])
            )}
          >
            {isBotUser(record.assigneeIds[0]) ? (
              <Bot size={14} aria-hidden="true" />
            ) : (
              initials(ctx.resolveUserName(record.assigneeIds[0]))
            )}
          </span>
        )}
      </div>
    </div>
  );
}
